/* eslint no-console: 0, no-cond-assign: 0 */

import mdast from 'mdast';
import html from 'mdast-html';
import {isEqual} from 'lodash';

const htmlProcessor = mdast().use(html);

const regionFilterRegex = /\{r\/([^\/:}]*)\/?([^\/:}]+)?\/?([^:}]+)?:?([^}]+)?}/g;

export default class {

  constructor (markdownData) {
    this.doc = mdast().parse(markdownData);
    this.filteredDoc = null;
    this.regionData = null;
  }

  getSectionNames () {
    const names = [];
    const { children } = this.doc;
    for (const child of children) {
      if (
        child.type === 'heading' &&
        child.depth === 1 &&
        child.children &&
        child.children.length > 0 &&
        child.children[0] &&
        typeof child.children[0].value === 'string'
      ) {
        names.push(child.children[0].value);
      }
    }
    return names;
  }

  getSectionAsHtml(sectionName, includeHeader, regionData) {
    const section = this._getSection(sectionName, includeHeader, regionData);
    return this._asHtml(section);
  }

  getSectionAsHtmlWithPrefixedUrls(sectionName, includeHeader, prefix, regionData) {
    const section = this._getSection(sectionName, includeHeader, regionData);
    return this._asHtmlWithPrefixedUrls(section, prefix);
  }

  _getSubSection (sectionName, subSectionName, includeHeader, regionData) {
    const { type, children, position } = this._getSection(sectionName, false, regionData);
    const regex = new RegExp(`^${subSectionName}$`, 'i');
    const newNodes = {
      type,
      position,
      children: []
    };
    let inSection = false;
    for (const child of children) {
      if (
        child.type === 'heading' &&
        child.depth === 2 &&
        child.children &&
        child.children.length > 0 &&
        child.children[0] &&
        typeof child.children[0].value === 'string'
      ) {
        if (inSection) {
          break;
        } else if (child.children[0].value.match(regex)) {
          inSection = true;
          if (includeHeader) {
            newNodes.children.push(child);
          }
          continue;
        }
      }
      if (inSection) {
        newNodes.children.push(child);
      }
    }

    if (newNodes.children && newNodes.children.length === 0) {
      console.log('No markdown section/subsection found for', sectionName,
        subSectionName);
      throw new Error(`No markdown section/subsection found for` +
        ` ${sectionName} ${subSectionName}`);
    }

    return newNodes;
  }

  getSectionAsString (sectionName, includeHeader, regionData) {
    return mdast.stringify(
      this._getSection(sectionName, includeHeader, regionData)
    ).trim();
  }

  getSubSectionAsString (sectionName, subSectionName, includeHeader, regionData) {
    const subSection = this._getSubSection(sectionName, subSectionName, includeHeader, regionData);
    const result = mdast.stringify(subSection).trim();
    return result;
  }

  getSubSectionAsHtml (sectionName, subSectionName, includeHeader, regionData) {
    const subSection = this.getSubSectionAsString(sectionName, subSectionName, includeHeader, regionData);
    const result = htmlProcessor.process(subSection);
    return result;
  }

  getAllAsHtml (regionData) {
    const doc = this._getFilteredDocForRegion(regionData);
    return htmlProcessor.process(mdast.stringify(doc));
  }

  getAllAsHtmlWithPrefixedUrls (prefix, regionData) {
    const doc = this._getFilteredDocForRegion(regionData);
    return this._asHtmlWithPrefixedUrls(doc, prefix);
  }

  getQuestions (sectionName, subSectionName, regionData) {
    // FIXME: Not very forgiving parsing
    const subSection = this._getSubSection(sectionName, subSectionName, false, regionData);
    const questions = [];
    const questionNodes = subSection.children[0].children;
    questionNodes.forEach((node, index) => {
      const value = node.children[0].children[0].value;
      let text = value, exception = false, cumulative = false;
      switch(value[0]) {
        case '*':
          text = value.substr(1);
          exception = true;
          break;
        case '+':
          text = value.substr(1);
          cumulative = true;
          break;
        default:
          break;
      }
      questions.push({number: index + 1, exception, cumulative, text});
    });
    return questions;
  }

  getSubSectionImage (sectionName, subSectionName) {
    const subSection = this._getSubSection(sectionName, subSectionName);
    let imageUrl;
    try {
      const paragraph = subSection.children[0];
      if (paragraph.type !== 'paragraph') {
        throw new Error('Not a paragraph');
      }
      const image = paragraph.children[0];
      if (image.type !== 'image') {
        throw new Error('Not an image');
      }
      imageUrl = image.src;
    } catch (err) {
      console.error('Unexpected input', err);
    }
    if ( !imageUrl ) {
      console.error(`Image not found for ${sectionName} ${subSectionName}`);
      throw new Error(`Image not found for` +
        ` ${sectionName} ${subSectionName}`);
    }
    return imageUrl;
  }

  getSectionMetadata (sectionName, key, regionData) {
    const metadata = this._getSection(sectionName, false, regionData);
    const keyValues = mdast.stringify(metadata).split('\n');
    const regex = new RegExp('^(' + key + ')\s*:\s*(.*)\s*$', 'i');
    for (const keyValue of keyValues) {
      const match = keyValue.match(regex);
      if (match && match.length > 1) {
        return match[2].trim();
      }
    }
  }

  getMetadata (key) {
    return this.getSectionMetadata('metadata', key);
  }

  _getSection (sectionName, includeHeader, regionData) {
    const doc = this._getFilteredDocForRegion(regionData);
    const { type, children, position } = doc;
    const regex = new RegExp(`^${sectionName}$`, 'i');
    const newNodes = {
      type,
      position,
      children: []
    };
    let inSection = false;
    for (const child of children) {
      if (
        child.type === 'heading' &&
        child.depth === 1 &&
        child.children &&
        child.children.length > 0 &&
        child.children[0] &&
        typeof child.children[0].value === 'string'
      ) {
        if (inSection) {
          break;
        } else if (child.children[0].value.match(regex)) {
          inSection = true;
          if (includeHeader) {
            newNodes.children.push(child);
          }
          continue;
        }
      }
      if (inSection) {
        newNodes.children.push(child);
      }
    }

    if (newNodes.children && newNodes.children.length === 0) {
      console.log('No markdown section found for', sectionName);
      throw new Error(`No markdown section found for ${sectionName}`);
    }

    return newNodes;
  }

  _asHtml(src) {
    const srcAsString = mdast.stringify(src);
    return htmlProcessor.process(srcAsString);
  }

  _asHtmlWithPrefixedUrls (src, prefix) {
    function prefixHref (doc, hrefPrefix) {
      const node = { ...doc };
      if (node.href && node.href.indexOf('http') !== 0) {
        node.href = hrefPrefix + node.href;
      }
      if (node.children) {
        node.children = node.children.map(childDoc =>
          prefixHref(childDoc, hrefPrefix));
      }
      return node;
    }
    const newDoc = prefixHref(src, prefix);

    const prefixedHtml = htmlProcessor.process(mdast.stringify(newDoc));
    const result = prefixedHtml.replace(
      new RegExp('<a href', 'g'),
      '<a target="_blank" href'
    );
    return result;
  }

  _matchRegionFilters(text) {
    const inlineFilters = [];
    let filter;
    let match;
    while (match = regionFilterRegex.exec(text)) {
      if (match.index === 0 && !match[4]) {
        filter = match;
        continue;
      }
      inlineFilters.push(match);
    }
    if (!filter && inlineFilters.length === 0) {
      return null;
    }
    return {
      inlineFilters: inlineFilters,
      filter: filter
    };
  }

  _matchRegion(filterMatch, regionData) {
    const {country, stateOrProvince, cityOrMunicipality, contractId} = regionData;
    if (filterMatch[1] && filterMatch[1] === contractId) return true;
    if (filterMatch[1] && filterMatch[1] !== country) return false;
    if (filterMatch[2] && filterMatch[2] !== stateOrProvince) return false;
    if (filterMatch[3] && filterMatch[3] !== cityOrMunicipality) return false;
    return true;
  }

  _applyRegionFilters(textNode, regionData, nodeGroup) {
    const {value, ...restOfTextNode} = textNode;
    const regionFilters = this._matchRegionFilters(value);
    if (!regionFilters) {
      return textNode;
    }
    let filteredText = value;
    if (regionFilters.filter) {
      if (nodeGroup && !nodeGroup.hasMatch && regionFilters.filter[1] === '') {
        nodeGroup.hasMatch = false;
        filteredText = filteredText.substr(regionFilters.filter.index + regionFilters.filter[0].length);
      } else if (!nodeGroup && regionFilters.filter[1] === '') {
        filteredText = filteredText.substr(regionFilters.filter.index + regionFilters.filter[0].length);
      } else if (regionFilters.filter[1] !== '' && this._matchRegion(regionFilters.filter, regionData)) {
        if (nodeGroup) nodeGroup.hasMatch = true;
        filteredText = filteredText.substr(regionFilters.filter.index + regionFilters.filter[0].length);
      } else {
        return null;
      }
    }
    let hasMatch = false;
    filteredText = regionFilters.inlineFilters.reduce((text, filter) => {
      let replaceWith = '';
      const hasEmptyFilter = filter[1] === '';
      if ((hasEmptyFilter && !hasMatch) || (!hasEmptyFilter && this._matchRegion(filter, regionData))) {
        hasMatch = !hasEmptyFilter;
        replaceWith = filter[4];
      }
      if (replaceWith) {
        replaceWith = replaceWith.replace('$cityOrMunicipality', regionData.cityOrMunicipality)
          .replace('$stateOrProvince', regionData.stateOrProvince)
          .replace('$country', regionData.country)
          .replace('$contractId', regionData.contractId)
      }
      return text.replace(filter[0], replaceWith);
    }, filteredText);
    return {
      value: filteredText,
      ...restOfTextNode
    };
  }

  _getFilteredNodeForRegion(node, regionData) {
    if (!regionData) return node;
    const { children, ...restOfNode } = node;
    if (!children) return node;
    const newNode = {
      ...restOfNode,
      children: []
    };
    function appendIfNotEmpty(item) {
      if (item.children && item.children.length > 0) {
        newNode.children.push(item);
      }
    }
    const headingGroup = { hasMatch: false };
    const paragraphGroup = { hasMatch: false };
    const listItemGroup = { hasMatch: false };
    for (const child of children) {
      switch (child.type) {
      case 'text':
        const filteredTextNode = this._applyRegionFilters(child, regionData);
        if (filteredTextNode) {
          newNode.children.push(filteredTextNode);
        }
        break;
      case 'heading':
        if (!child.children) {
          newNode.children.push(child);
          break;
        }
        if (child.children[0].type !== 'text') {
          appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
          break;
        }
        const headingTextNode = this._applyRegionFilters(child.children[0], regionData, headingGroup);
        if (headingTextNode) {
          appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
        }
        break;
      case 'paragraph':
        if (!child.children) {
          newNode.children.push(child);
          break;
        }
        if (child.children[0].type !== 'text') {
          appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
          break;
        }
        const paragraphTextNode = this._applyRegionFilters(child.children[0], regionData, paragraphGroup);
        if (paragraphTextNode) {
          appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
        }
        break;
      case 'list':
        appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
        break;
      case 'listItem':
        if (!child.children) {
          newNode.children.push(child);
          break;
        }
        if (child.children[0].type !== 'paragraph') {
          appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
          break;
        }
        const listItemTextNode = this._applyRegionFilters(child.children[0].children[0], regionData, listItemGroup);
        if (listItemTextNode) {
          appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
        }
        break;
      default:
        appendIfNotEmpty(this._getFilteredNodeForRegion(child, regionData));
      }
    }
    return newNode;
  }

  _getFilteredDocForRegion(regionData) {
    if (!regionData) {
      return this.doc;
    }
    if (isEqual(regionData, this.regionData) && this.filteredDoc) {
      return this.filteredDoc;
    }
    this.regionData = regionData;
    this.filteredDoc = this._getFilteredNodeForRegion(this.doc, regionData);
    return this.filteredDoc;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/markdownScript.js